import Box from '@mui/material/Box'
import ReportIcon from '@mui/icons-material/Report'
import Grid from '@mui/material/Grid2'
import { useContext } from 'react'
import _ from 'lodash'
import CaleoSecondaryButton from '../Buttons/CaleoSecondaryButton'
import CardContentText from '../CaleoCustomComponents/CardContentText'
import ModalPaper from '../CaleoCustomComponents/ModalPaper'
import { getLanguageDataMap, chooseDBTranslation, Translateable, TranslateableObject } from 'utils/translations'
import { AnyObject, ContextValue } from './types'
import { Context } from './context'

/**
 * Data edit context translation import component.
 */
export const DataEditContextTranslationImport = <T extends AnyObject>() => {
  const { data, hasTranslation, setFields, reloadTranslation, i18n, t, valid } = useContext<ContextValue<T>>(Context)

  if (hasTranslation) {
    return null
  }

  const createTranslation = async (isNew?: boolean) => {
    const languageData = await getLanguageDataMap()
    const newLanguage = languageData[i18n.language]
    const translation = chooseDBTranslation(i18n, data as unknown as TranslateableObject<Translateable>)
    const objectKeys: Record<string, unknown> = { ...(_.cloneDeep(translation) as Record<string, unknown>) }
    if (isNew) {
      for (const [key] of Object.entries(objectKeys)) {
        if (key !== 'id' && key !== 'Language') {
          objectKeys[key] = ''
        }
      }
    }
    const newTranslation = {
      ..._.cloneDeep(objectKeys),
      id: null,
      languageId: newLanguage.id,
      Language: newLanguage,
      createdAt: new Date(),
      updatedAt: new Date(),
    }

    setFields({
      translations: [...(data as unknown as TranslateableObject<Translateable>).translations, newTranslation],
    })

    reloadTranslation()
  }

  const showImportInstructions = () => {
    return (
      <Grid container justifyContent="center" alignItems="center" direction="column">
        <Grid>
          <Grid container justifyContent="center" alignItems="center">
            <Grid>
              <ReportIcon color="error" fontSize="large" />
            </Grid>
            <Grid>
              <h4>{t('translationImport.missing', { lang: i18n.language.toUpperCase() })}</h4>
            </Grid>
          </Grid>
        </Grid>
        <Grid style={{ marginBottom: 15 }}>
          <CardContentText type="description">
            {t('translationImport.description', { lang: i18n.language.toUpperCase() })}
          </CardContentText>
        </Grid>
        <Grid>
          <Grid container justifyContent="center" alignItems="center" spacing={1}>
            <Grid>
              <CaleoSecondaryButton
                id="addNewTranslation"
                testId="button-data-context-add-new-translation"
                clickAction={() => createTranslation(true)}
                label={t('translationImport.addNew', { lang: existingTranslation.Language?.name.toUpperCase() })}
                valid={true}
              />
            </Grid>
            <Grid>
              <CaleoSecondaryButton
                id="copyOldTranslation"
                testId="button-data-context-copy-old-translation"
                clickAction={() => createTranslation()}
                label={t('translationImport.addOld', { lang: existingTranslation.Language?.name.toUpperCase() })}
                valid={true}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const showError = () => {
    return (
      <Grid container justifyContent="center" alignItems="center">
        <Grid>
          <ReportIcon color="error" fontSize="large" />
        </Grid>
        <Grid>
          <h4>{t('translationImport.dataMissing')}</h4>
        </Grid>
      </Grid>
    )
  }

  const showInfo = () => {
    return (
      <Grid container justifyContent="center" alignItems="center">
        <Grid>
          <ReportIcon color="error" fontSize="large" />
        </Grid>
        <Grid>
          <h4>{t('translationImport.noTranslation')}</h4>
        </Grid>
      </Grid>
    )
  }

  const existingTranslation = chooseDBTranslation(i18n, data as unknown as TranslateableObject<Translateable>)

  return (
    <Box p={{ md: 3, sm: 1, xs: 0 }} mt={{ xs: 1 }}>
      {valid ? (
        <ModalPaper>{existingTranslation.Language ? showImportInstructions() : showError()}</ModalPaper>
      ) : (
        <ModalPaper>{showInfo()}</ModalPaper>
      )}
    </Box>
  )
}
